import { Alert } from 'domain/models';
import { WithAlertInfo } from 'domain/models/api.models';
import { useMemo } from 'react';

/**
 * TIME CONSTANTS
 */
export const DATE_TIME_S_FORMAT = 'dd/MM/yyyy HH:mm';
export const DATE_TIME_FORMAT = 'dd/MM/yyyy HH:mm:ss';
export const DATE_FORMAT = 'dd/MM/yyyy';
export const TIME_FORMAT = 'HH:mm:ss';
/**
 * Util class for getting the elapsed time and the unit.
 * - Less than 5 minutes: seconds
 * - Less than 1 hour: minutes
 * - Less than 1 day: hours
 * - More than 1 day: days
 */
export const timeElapsedUTC = (date?: Date | string): [number, string] => {
	if (!date) {
		return [0, 'unknown'];
	}
	const now = new Date();
	const parsed = new Date(date); // This is UTC, not local time
	// If now or parsed are invalid dates, return 0
	if (isNaN(now.getTime()) || isNaN(parsed.getTime())) {
		return [0, 'unknown'];
	}

	let diff = now.getTime() - parsed.getTime();

	if (diff < 0) {
		diff = -diff;
	}
	const seconds = Math.floor(diff / 1000);
	const minutes = Math.floor(seconds / 60);
	const hours = Math.floor(minutes / 60);
	const days = Math.floor(hours / 24);

	if (seconds < 300) {
		return [seconds, 'seconds'];
	}

	if (minutes < 60) {
		return [minutes, 'minutes'];
	}

	if (hours < 24) {
		return [hours, 'hours'];
	}

	return [days, 'days'];
};

/**
 * Badge color status
 */
export const useBadge = (elapsed: [number, string]) => {
	return useMemo(
		() =>
			['seconds', 'minutes'].includes(elapsed[1])
				? 'OK'
				: 'hours' === elapsed[1]
				? 'WARN'
				: 'unknown' === elapsed[1]
				? 'UNKNOWN'
				: 'ERROR',
		[elapsed]
	);
};

/**
 * Badge for alerts
 */
export const useAlertBadge = (
	alert: WithAlertInfo
): {
	status: 'OK' | 'WARN' | 'ERROR';
	lastDate: number | string | Date | undefined;
} => {
	const status = useMemo(() => {
		if (alert?.alertCount === 0) {
			return 'OK';
		}
		if (alert?.alertCount === 0) {
			return 'OK';
		}

		if (alert?.alertCount < 5) {
			return 'WARN';
		}

		return 'ERROR';
	}, [alert?.alertCount]);

	const lastDate = useMemo(
		() =>
			alert?.alerts
				.map((a: Alert) => a.alertTime)
				.sort()
				.reverse()[0],
		[alert?.alerts]
	);

	return { status, lastDate };
};
